import type { ActiveLayoutItem } from '../../types/layout-items.tsx';
import type { FieldItemFragment as GraphqlFieldItem } from '../fragments/__generated_apollo__/FieldItemFragment';
import type { JiraIssueLayoutItemConfigurationFragment_items_nodes as GraphqlActionLayoutItemNode } from '../fragments/__generated_apollo__/JiraIssueLayoutItemConfigurationFragment';
import type {
	JiraIssueLayoutItemContainerFragment,
	JiraIssueLayoutItemContainerFragment_items_nodes as GraphqlContainerItemNode,
} from '../fragments/__generated_apollo__/JiraIssueLayoutItemContainerFragment';
import type { PanelItemFragment as GraphqlPanelItem } from '../fragments/__generated_apollo__/PanelItemFragment';
import type { ActiveLayoutItemsTransformer, Transformers } from './types.tsx';

/**
 * It may happen that a field or layout item is referenced but the actual data never arrives.
 * In case a customer adds a field to Field screen tab, then deletes it,
 * the tab will still remember the field reference even though the entity is gone.
 *
 * We should filter such references away.
 */
const getMatchingNode = <T,>(nodeMap: Map<string, T>, containerItemId: string): T | undefined =>
	nodeMap.get(containerItemId);

export const createLayoutItemsIndex = (
	nodes: ReadonlyArray<GraphqlActionLayoutItemNode>,
): {
	fieldsById: Map<string, GraphqlFieldItem>;
	panelsById: Map<string, GraphqlPanelItem>;
} => {
	const fieldsById = new Map<string, GraphqlFieldItem>();
	const panelsById = new Map<string, GraphqlPanelItem>();
	for (let i = 0; i < nodes.length; i += 1) {
		const node = nodes[i];
		if (node.__typename === 'JiraIssueLayoutFieldItemConfiguration') {
			fieldsById.set(node.fieldItemId, node);
		}
		if (node.__typename === 'JiraIssueLayoutPanelItemConfiguration') {
			panelsById.set(node.panelItemId, node);
		}
	}

	return { fieldsById, panelsById };
};

type ContainerNodeWithContainerType = {
	containerType: string;
	containerPosition: number | undefined;
	node: GraphqlContainerItemNode;
};
/**
 * Transform layout item container data from GraphQL into SWIFT compatible {@link ActiveLayoutItem} objects.
 */
export const transformActiveLayoutItems: ActiveLayoutItemsTransformer = (
	containers: ReadonlyArray<JiraIssueLayoutItemContainerFragment | null | undefined>,
	fieldsById: Map<string, GraphqlFieldItem>,
	panelsById: Map<string, GraphqlPanelItem>,
	transformers: Transformers,
): ActiveLayoutItem[] => {
	// Flatten and sort all our container nodes by their container position
	const containerNodesWithContainerType = containers
		// TODO MCD-2079 Update schema types so containers does not allow nullish items
		.filter(Boolean)
		.reduce<Array<ContainerNodeWithContainerType>>(
			(
				accum: ContainerNodeWithContainerType[],
				container: JiraIssueLayoutItemContainerFragment,
			) => {
				if (!container || !container.containerType || !container.items || !container.items.nodes) {
					throw new Error('Failed to load initial data. No containers for active layout items');
				}

				const {
					containerType,
					items: { nodes },
				} = container;

				const nodesWithCategory = nodes
					// TODO MCD-2079 Update schema types so containers does not allow nullish items
					.filter(Boolean)
					.map((node) => ({
						containerType,
						node,
						containerPosition:
							node?.__typename === 'JiraIssueItemFieldItem'
								? // TODO MCD-2079 We want all layout items to return this data
									node.containerPosition
								: undefined,
					}));

				return accum.concat(nodesWithCategory);
			},
			[],
		)
		// Sort the flattened list by container position. Currently only fields have this data so if it's undefined for
		// either item then retain the original sort order.
		// TODO This logic exists purely for JSM CMP to support merging data from multiple containers into a single
		//  category in the UI. Ideally this logic should live in JSM transformer code (or better yet abolish category merging).
		.sort(({ containerPosition: a }, { containerPosition: b }) =>
			a === undefined || b === undefined ? 0 : a - b,
		);

	return containerNodesWithContainerType
		.flatMap((containerNodeWithContainerType) => {
			const { containerType, node } = containerNodeWithContainerType;

			switch (node.__typename) {
				case 'JiraIssueItemFieldItem': {
					const matchingNode = getMatchingNode(fieldsById, node.fieldItemId);
					if (!matchingNode) {
						return null;
					}

					return transformers.activeLayoutItem(matchingNode, containerType, transformers);
				}
				case 'JiraIssueItemPanelItem': {
					const matchingNode = getMatchingNode(panelsById, node.panelItemId);
					if (!matchingNode) {
						return null;
					}
					return transformers.activeLayoutItem(matchingNode, containerType, transformers);
				}
				case 'JiraIssueItemTabContainer': {
					// Screen tabs cannot have panels in them; only fields.
					if (node.items == null || node.items.nodes == null) {
						return null;
					}

					return node.items.nodes
						.filter(Boolean)
						.map(({ fieldItemId }) => getMatchingNode(fieldsById, fieldItemId))
						.filter(Boolean)
						.map((matchingNode) =>
							transformers.activeLayoutItem(matchingNode, containerType, transformers),
						);
				}
				default:
					// We ignore all unsupported layout items as we expect them to be mapped separately, e.g. tabs.
					return null;
			}
		})
		.filter(Boolean);
};
